import { z } from 'zod';
import type {
  SrdRace,
  SrdSpell,
  SrdEquipmentItem,
  SrdClass,
  SrdSubclass,
} from './types.js';

/**
 * Single source of truth for the shape of `HomebrewSessionEntry.data`,
 * expressed as per-entry-type zod schemas.
 *
 * Modeled on shared/systems/dnd5e/sheet-values-schema.ts (SheetValuesSchema
 * record+superRefine pattern — read as reference, not modified here).
 *
 * Each schema is symmetric to the extractor output shape in
 * server/src/ai/extraction/schemas/homebrew-schema.ts (homebrewOutputSchema)
 * — the F4 invariant. Optional fields carry defaults verbatim from the current
 * `?? default` call sites (R1): race speed default 30, spell school 'Evocation',
 * classes []. Coercion is intentionally NOT applied to numeric fields so a
 * string like '30ft' falls through to the default rather than being parsed
 * (R2/R3 repro).
 */

/**
 * HomebrewEntryType discriminant — byte-for-byte from
 * shared/utils.d.ts:67 (`'class' | 'subclass' | 'spell' | 'feat' | 'race' | 'item'`).
 *
 * Derived inline (not imported) because shared/domain.ts does NOT export
 * this symbol (ADR-050 import-name-gate): re-importing it there fails TS2305.
 */
export type HomebrewEntryType =
  | 'class'
  | 'subclass'
  | 'spell'
  | 'feat'
  | 'race'
  | 'item';

/**
 * A single trait entry as emitted by the homebrew extractor
 * (homebrew-schema.ts). Kept permissive; extractor emits `{ name, description }`.
 */
const HomebrewTraitSchema = z
  .object({
    name: z.string(),
    description: z.string(),
  })
  .passthrough();

/**
 * Speed sub-shape for a race. `walk` mirrors the raw extractor field; the
 * top-level `speed` default of 30 is applied by HomebrewRaceDataSchema.
 */
const HomebrewSpeedSchema = z
  .object({
    walk: z.number().optional(),
    fly: z.number().optional(),
    swim: z.number().optional(),
    climb: z.number().optional(),
    burrow: z.number().optional(),
    hover: z.boolean().optional(),
  })
  .partial()
  .passthrough();

/**
 * RACE — projects onto SrdRace (srd/types.ts:293).
 * `speed` default is 30 (number). Coercion is deliberately absent so that a
 * non-number value (e.g. '30ft') is rejected by the field schema and the
 * default is used instead (R2/R3-repro), mirroring homebrew-srd.ts:111
 * `(e.data.speed as number) ?? 30` and the session-srd.ts:271 duplicate.
 */
export const HomebrewRaceDataSchema = z
  .object({
    name: z.string().optional(),
    size: z.string().optional(),
    speed: z.number().default(30),
    speeds: HomebrewSpeedSchema.optional(),
    // SrdRace.abilityBonuses = SrdAbilityBonus[] ([{ability, bonus}], extractor
    // R: "MUST be [{ability, bonus}], NEVER a record/string") — record-форма
    // отвергала валидный массив → бонус терялся (regression fix 28.07).
    abilityBonuses: z.array(z.object({ ability: z.string(), bonus: z.number() })).default([]),
    traits: z.array(HomebrewTraitSchema).default([]),
    languages: z.array(z.string()).default([]),
    subraces: z.array(z.unknown()).default([]),
    description: z.string().optional(),
  })
  .passthrough();

/**
 * SPELL — projects onto SrdSpell (srd/types.ts:148).
 * `school` default is 'Evocation' verbatim.
 */
export const HomebrewSpellDataSchema = z
  .object({
    name: z.string().optional(),
    level: z.number().optional(),
    school: z.string().default('Evocation'),
    castingTime: z.string().optional(),
    range: z.string().optional(),
    components: z.string().default('V, S'),
    duration: z.string().optional(),
    description: z.string().optional(),
    higherLevel: z.string().optional(),
    ritual: z.boolean().optional(),
    concentration: z.boolean().optional(),
    classes: z.array(z.string()).default([]),
  })
  .passthrough();

/**
 * Nested weapon sub-shape — RAW form as in
 * server/src/ai/extraction/schemas/homebrew-schema.ts:203-222 (R5).
 */
const HomebrewWeaponDataSchema = z
  .object({
    damage: z.string().optional(),
    damageType: z.string().optional(),
    properties: z.array(z.string()).default([]),
    range: z.string().optional(),
    category: z.string().optional(),
  })
  .passthrough();

/**
 * Nested armor sub-shape — RAW form as in
 * server/src/ai/extraction/schemas/homebrew-schema.ts:203-222 (R5).
 */
const HomebrewArmorDataSchema = z
  .object({
    baseAc: z.number().optional(),
    addDex: z.boolean().optional(),
    maxDex: z.number().optional(),
    category: z.string().optional(),
    stealthDisadvantage: z.boolean().optional(),
  })
  .passthrough();

/**
 * ITEM — projects onto SrdEquipmentItem (srd/types.ts:184).
 * RAW shape retains nested weaponData / armorData (R5) exactly as the
 * extractor emits them.
 */
export const HomebrewItemDataSchema = z
  .object({
    name: z.string().optional(),
    type: z.string().optional(),
    rarity: z.string().optional(),
    weight: z.number().optional(),
    cost: z.string().optional(),
    description: z.string().optional(),
    requiresAttunement: z.boolean().optional(),
    weaponData: HomebrewWeaponDataSchema.optional(),
    armorData: HomebrewArmorDataSchema.optional(),
  })
  .passthrough();

/**
 * CLASS — projects onto SrdClass (srd/types.ts).
 */
export const HomebrewClassDataSchema = z
  .object({
    name: z.string().optional(),
    hitDie: z.number().optional(),
    primaryAbility: z.array(z.string()).default([]),
    savingThrows: z.array(z.string()).default([]),
    // NO default here: homebrewClassDataToSrdClass (homebrew-class-mapper.ts:42
    // mergeProficiencies) treats the ABSENCE of `proficiencies` as the signal to
    // merge the raw extractor arrays (armorProficiencies/weaponProficiencies/
    // toolProficiencies). Injecting `proficiencies: []` here short-circuits that
    // merge → the engine gets []. Raw extractor fields survive via .passthrough().
    proficiencies: z.array(z.string()).optional(),
    features: z.array(HomebrewTraitSchema).default([]),
    spellcasting: z.unknown().optional(),
    description: z.string().optional(),
  })
  .passthrough();

/**
 * SUBCLASS — projects onto SrdSubclass (srd/types.ts).
 */
export const HomebrewSubclassDataSchema = z
  .object({
    name: z.string().optional(),
    parentClass: z.string().optional(),
    features: z.array(HomebrewTraitSchema).default([]),
    spellcasting: z.unknown().optional(),
    description: z.string().optional(),
  })
  .passthrough();

export type HomebrewRaceData = z.infer<typeof HomebrewRaceDataSchema>;
export type HomebrewSpellData = z.infer<typeof HomebrewSpellDataSchema>;
export type HomebrewItemData = z.infer<typeof HomebrewItemDataSchema>;
export type HomebrewClassData = z.infer<typeof HomebrewClassDataSchema>;
export type HomebrewSubclassData = z.infer<typeof HomebrewSubclassDataSchema>;

/**
 * Result of parseHomebrewData: either a validated value or a hard failure.
 * Individual bad typed fields do NOT reject the entry — they fall to their
 * default with a console.warn (R2 policy: coerce fields, not reject entry).
 * `ok:false` is only returned for entry types with no schema (e.g. 'feat').
 */
export type ParseHomebrewDataResult<T> =
  | { ok: true; value: T }
  | { ok: false };

interface ParseHomebrewContext {
  packageId?: string;
  slug?: string;
}

/**
 * Runs safeParse against the type-specific schema. On per-field failure the
 * schema's own `.default(...)` already substitutes the default; this wrapper
 * additionally surfaces the drift via console.warn(packageId/slug/field) so
 * the entry is preserved rather than dropped (R2 policy).
 */
function runSchema<T>(
  schema: z.ZodType<T>,
  data: Record<string, unknown>,
  ctx: ParseHomebrewContext | undefined,
): ParseHomebrewDataResult<T> {
  const result = schema.safeParse(data);
  if (result.success) {
    return { ok: true, value: result.data };
  }

  // Field-level drift: warn per offending field, then re-parse a sanitized
  // copy with the bad fields removed so their schema-level defaults apply.
  const sanitized: Record<string, unknown> = { ...data };
  for (const issue of result.error.issues) {
    const field = issue.path.length > 0 ? String(issue.path[0]) : '(root)';
    console.warn(
      `[homebrew-data-schema] dropping invalid field "${field}"` +
        ` (packageId=${ctx?.packageId ?? '?'}, slug=${ctx?.slug ?? '?'}):` +
        ` ${issue.message}`,
    );
    if (issue.path.length === 1) {
      delete sanitized[field];
    }
  }

  const retry = schema.safeParse(sanitized);
  if (retry.success) {
    return { ok: true, value: retry.data };
  }
  return { ok: false };
}

/**
 * Selects the per-type schema by the HomebrewEntryType discriminant and
 * validates `data`. Values of `type` are byte-for-byte from
 * shared/utils.d.ts:67 and the extractor enum (homebrew-schema.ts:36).
 *
 * 'feat' has no dedicated data schema in this source of truth, so it returns
 * `{ ok: false }` (caller keeps the raw entry as-is).
 */
export function parseHomebrewData(
  type: HomebrewEntryType,
  data: Record<string, unknown>,
  ctx?: ParseHomebrewContext,
): ParseHomebrewDataResult<
  | HomebrewRaceData
  | HomebrewSpellData
  | HomebrewItemData
  | HomebrewClassData
  | HomebrewSubclassData
> {
  switch (type) {
    case 'race':
      return runSchema(HomebrewRaceDataSchema, data, ctx);
    case 'spell':
      return runSchema(HomebrewSpellDataSchema, data, ctx);
    case 'item':
      return runSchema(HomebrewItemDataSchema, data, ctx);
    case 'class':
      return runSchema(HomebrewClassDataSchema, data, ctx);
    case 'subclass':
      return runSchema(HomebrewSubclassDataSchema, data, ctx);
    case 'feat':
      return { ok: false };
    default: {
      const _exhaustive: never = type;
      return { ok: false };
    }
  }
}

/**
 * Projection helpers — narrow the validated homebrew data onto the SRD types
 * consumed by the rest of the system. These are structural mappings only; the
 * SRD types (SrdRace, SrdSpell, SrdEquipmentItem, SrdClass, SrdSubclass) remain
 * the target shapes referenced from srd/types.ts.
 */
export type HomebrewRaceProjection = Partial<SrdRace>;
export type HomebrewSpellProjection = Partial<SrdSpell>;
export type HomebrewItemProjection = Partial<SrdEquipmentItem>;
export type HomebrewClassProjection = Partial<SrdClass>;
export type HomebrewSubclassProjection = Partial<SrdSubclass>;